import React from 'react';
import { Box, Typography, styled } from '@mui/material';

export interface ToggleOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

export interface SegmentedToggleProps<T extends string> {
  options: ToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}

const Container = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  backgroundColor: theme.palette.principal.grey30,
  borderRadius: '4px',
  padding: '4px',
}));

const Segment = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isDisabled',
})<{ isSelected: boolean; isDisabled: boolean }>(({ theme, isSelected, isDisabled }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 24px',
  borderRadius: '4px',
  border: 'none',
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s ease',
  background: isSelected ? theme.palette.principal.white : 'transparent',
  boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
  '& .MuiTypography-root': {
    color: isDisabled
      ? theme.palette.principal.grey70
      : isSelected
      ? theme.palette.brand.jungle
      : theme.palette.principal.grey70,
  },

  '& svg': {
    color: isDisabled
      ? theme.palette.principal.grey70
      : isSelected
      ? theme.palette.brand.jungle
      : theme.palette.principal.grey70,
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.brand.jungle}`,
    outlineOffset: '2px',
  },
}));

export const SegmentedToggle = <T extends string>({
  options,
  value,
  onChange,
  disabled = false,
}: SegmentedToggleProps<T>): React.ReactElement => {
  const handleClick = (optionValue: T) => {
    if (!disabled && optionValue !== value) {
      onChange(optionValue);
    }
  };

  return (
    <Box>
      <Container>
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <Segment
              key={option.value}
              type="button"
              isSelected={isSelected}
              isDisabled={disabled}
              onClick={() => handleClick(option.value)}
              aria-pressed={isSelected}
              disabled={disabled}
            >
              {option.icon}
              <Typography variant="bodySingle">{option.label}</Typography>
            </Segment>
          );
        })}
      </Container>
    </Box>
  );
};
